/*
 * product-group-selector.js — `<product-group-selector-component>`
 * custom element. Two responsibilities:
 *
 *   1. Hover label swap. The `.product-group-selector__name`
 *      span shows the current product's colour name by
 *      default; hovering a swatch / pill swaps it to that
 *      sibling's name, reverts on mouse-leave. Mirrors
 *      v3.0's product-group-selector.liquid behaviour.
 *
 *   2. Dropdown navigate. When the block is in `dropdown`
 *      mode it embeds a `<custom-select-component>` whose
 *      values are sibling product URLs. Listen for the
 *      custom-select's `change` event (it bubbles via the
 *      inner hidden input) and navigate to the picked URL.
 *
 * Loaded as `type="module"` from snippets/scripts.liquid
 * inside the product-template gate. Self-init: each
 * `<product-group-selector-component>` element runs its own
 * connectedCallback when the upgrade lands.
 */

// Alternate-template view suffix → templates/product.metal-switch.liquid.
// `?view=metal-switch` returns a bare `layout none` data payload — product
// JSON, iJewel map, metafields and ring-size map, with no section chrome.
// Matches the theme's other data-only views (`?view=variants`,
// `?view=ijewel-cad`): one idiom for "fetch a bare product payload".
const METAL_SWITCH_VIEW = 'metal-switch';

class ProductGroupSelectorComponent extends HTMLElement {
  /** @type {Map<string, {productJson: string, mapText: string}>} */
  #cache = new Map();
  /** @type {AbortController | undefined} — aborts an in-flight metal switch */
  #switchAbort;

  connectedCallback() {
    this._bindHoverLabel();
    this._bindSummarySlot();
    this._bindDropdownNavigate();
    this._bindAjaxSwitch();
  }

  /**
   * Stage the flush summary slot only when Metal closes after a real
   * selection — staging while open reflows the header label.
   */
  _stageSummarySlot() {
    const details = this.querySelector('details.product-option-accordion');
    if (!details || !this._swatchPopulated) return;
    if (details.classList.contains('product-option-accordion--slot')) return;
    details.classList.add('product-option-accordion--slot');
  }

  _bindSummarySlot() {
    const details = this.querySelector('details.product-option-accordion');
    if (!details) return;

    details.addEventListener('toggle', () => {
      if (!details.open && this._swatchPopulated) this._stageSummarySlot();
    });
  }

  _bindHoverLabel() {
    const nameEl = this.querySelector('[data-current-name]');
    if (!nameEl) return;

    const swatchEl = this.querySelector('[data-current-swatch]');
    const priceEl = this.querySelector('[data-current-price]');
    // Track the committed name/price so mouse-leave restores them cleanly
    // after multiple hovers — and after an in-place metal switch updates
    // them (_setActive rewrites this._committed*).
    this._committedName = nameEl.textContent;
    this._committedSwatch = swatchEl?.getAttribute('style') || '';
    this._committedPrice = priceEl?.textContent || '';
    // Current product metal is already selected — treat as populated so
    // the summary swatch stages on first close (and hover can preview).
    if (this._committedSwatch || this.querySelector('[aria-current="true"]')) {
      this._swatchPopulated = true;
    }
    const getName = () => this._committedName || '';
    const getPrice = () => this._committedPrice || '';

    // Scope to elements inside the component carrying the
    // namespaced `data-pgs-name` attribute. The prefix matches
    // the `--pgs-*` CSS vars on the block root, so the attr
    // is unambiguously owned by this component — no risk of
    // colliding with `data-name` attributes any other block /
    // section / theme component might emit.
    const items = this.querySelectorAll('[data-pgs-name]');
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        nameEl.textContent = item.getAttribute('data-pgs-name') || getName();
        // Matches product-option-accordion.js's summarySwatchPopulated
        // gate: before the shopper has ever made a real selection here,
        // hover only previews name/price, not the swatch image — once
        // _setActive fires once, hover starts previewing the swatch too.
        if (this._swatchPopulated) {
          const bg = item.querySelector('.product-option-accordion__swatch-bg');
          if (bg) this._paintSwatch(bg.getAttribute('style') || '');
        }
        // Preview the sibling metal's own $ contribution (written by
        // product-price-contributions.js), matching what commits on switch.
        const siblingPrice = item.getAttribute('data-sibling-contribution');
        if (priceEl && siblingPrice != null) priceEl.textContent = siblingPrice;
      });
      item.addEventListener('mouseleave', () => {
        nameEl.textContent = getName();
        if (this._swatchPopulated) this._paintSwatch(this._committedSwatch || '');
        if (priceEl) priceEl.textContent = getPrice();
      });
    });
  }

  /**
   * Paint the current-metal swatch and keep the box's padding
   * modifier in sync — image fills go edge-to-edge, colour/gradient
   * fills keep the inset (see component-product-option-accordion.css).
   * @param {string} style
   */
  _paintSwatch(style) {
    const swatchEl = this.querySelector('[data-current-swatch]');
    if (!swatchEl) return;
    swatchEl.setAttribute('style', style || '');
    const boxEl = this.querySelector('[data-current-swatch-box]');
    boxEl?.classList.toggle(
      'product-option-accordion__summary-swatch-box--image',
      !!style && style.includes('background-image')
    );
  }

  _bindDropdownNavigate() {
    if (this.dataset.pickerType !== 'dropdown') return;

    // custom-select renders a `<custom-select-component>` host
    // with an inner hidden `<input>` carrying the selected
    // value. `change` events from that input bubble up the
    // host. The value we set per option in Liquid is the
    // sibling's URL, so navigating is a one-liner.
    const select = this.querySelector('custom-select-component');
    if (!select) return;

    select.addEventListener('change', (event) => {
      const target = event.target instanceof HTMLInputElement
        ? event.target
        : select.querySelector('input[type="hidden"]');
      const url = target?.value;
      if (url) window.location.href = url;
    });
  }

  /**
   * Combined-listing metal switch without navigation. Each pill / swatch
   * carries data-sibling-{handle,url,id,metal}. When the block flag is on we
   * intercept activation, fetch the sibling's data fragment (product JSON +
   * iJewel map), select the matching variant, and swap it in place — the 3D
   * gallery only re-applies its metal material (same cad_code → no GLB reload).
   * The `href` stays as the no-JS / flag-off fallback (full navigation).
   *
   * SCOPE — the switch updates ONLY the above-the-fold hero (title, price, ATC,
   * options, description/metafields, iJewel viewer). Below-fold sections (config
   * gallery, UGC, recommendations, FAQ) keep the server-rendered content of the
   * landed metal: across a combined listing the siblings differ only by metal,
   * so their below-fold content is effectively identical, and re-rendering it on
   * each switch would add a round-trip and risk tearing down live component
   * state (e.g. a Swiper) for content the shopper usually hasn't scrolled to. If
   * per-metal below-fold fidelity is ever required, refresh those sections
   * individually.
   */
  _bindAjaxSwitch() {
    if (this.dataset.ajaxSwitch === 'false') return;
    if (this.dataset.pickerType !== 'pills' && this.dataset.pickerType !== 'swatches') return;

    /** @type {HTMLAnchorElement[]} */
    this._siblings = Array.from(this.querySelectorAll('a[data-sibling-handle]'));
    if (!this._siblings.length) return;

    this._siblings.forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        // Honour new-tab / modified clicks — let the browser navigate.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        if (anchor.getAttribute('aria-current') === 'true') return; // already active
        // Optimistic pill — don't wait for network / matrix loads.
        this._setActive(anchor);
        this._switchTo(anchor).catch((error) => {
          if (error?.name === 'AbortError') return;
          console.error('metal switch failed — falling back to navigation', error);
          window.location.href = anchor.href;
        });
      });

      // Hover is real intent on ONE sibling — jump the idle warming queue
      // and pull its full paginated matrix now so the click is instant.
      anchor.addEventListener('mouseenter', () => {
        this._prefetchSibling(anchor, { fullMatrix: true });
      });
    });

    this._prefetchSiblings();
  }

  /**
   * Warm section payload (+ optionally paginated caches) for one sibling.
   * Returns the warm promise so _prefetchSiblings can stagger siblings.
   * @param {HTMLAnchorElement} anchor
   * @param {{fullMatrix?: boolean}} [opts] — fullMatrix pulls the paginated
   *   variant/ijewel-map matrix too (~2 pages at 250/page).
   * @returns {Promise<void>}
   */
  _prefetchSibling(anchor, { fullMatrix = false } = {}) {
    const handle = anchor.dataset.siblingHandle || '';
    return this._loadSibling(anchor)
      .then((payload) => {
        this._warmSiblingModel(payload);
        if (!fullMatrix || !handle || !window.ProductVariantMatrix?.prefetch) return;
        // Pass the sibling's own product JSON so prefetch() can check
        // needsFullMatrix() instead of assuming the worst case.
        let product;
        try {
          product = JSON.parse(payload.productJson);
        } catch (_e) {
          /* fall through with product undefined — same as before this fix */
        }
        return window.ProductVariantMatrix.prefetch(handle, product).catch(() => {});
      })
      .catch(() => {});
  }

  /**
   * Warm the GLB the sibling would actually need post-switch — matched by the
   * shopper's CURRENT cad (geometry), not every distinct model in the
   * sibling's map. Metal-only siblings share cad with the current product
   * (already warmed), so this is a no-op there; it only does real work for a
   * sibling whose geometry genuinely differs, which today only gets fetched
   * reactively, after the ajax swap, with the loading state showing.
   * @param {{mapText: string}} payload
   */
  _warmSiblingModel({ mapText }) {
    if (!window.iJewelPrefetch?.warm) return;
    const cad = this._currentCad();
    if (!cad) return;

    // Metal siblings share cad codes AND GLB urls, so if the shopper's current
    // geometry is already resolved there is nothing to fetch for the sibling —
    // recommit() will hand it straight over on switch.
    const known = window.IjewelCadResolver?.peek(cad);
    if (known?.url) {
      window.iJewelPrefetch.warm(known.url);
      return;
    }

    // Otherwise look for it in the sibling's seed entry (its selected variant).
    if (!mapText) return;
    let map;
    try {
      map = JSON.parse(mapText);
    } catch (_e) {
      return;
    }
    const entry = Object.values(map).find((v) => v?.cad === cad);
    if (entry?.url) window.iJewelPrefetch.warm(entry.url);
  }

  /**
   * On idle: light section payload for every non-active sibling first, then
   * their full matrices ONE sibling at a time. _switchTo awaits
   * matrixApi.loadAll() before it can swap, so a cold cache makes the metal
   * click visibly slow — and touch devices never get the hover warm.
   * Sequential chaining (not a parallel burst) keeps bandwidth free for the
   * shopper's own first option change / GLB fetch; at 250 variants per page
   * a sibling's matrix is only ~2 requests anyway.
   */
  _prefetchSiblings() {
    const run = () => {
      const idle = this._siblings.filter(
        (anchor) => anchor.getAttribute('aria-current') !== 'true'
      );
      idle.forEach((anchor) => this._prefetchSibling(anchor));
      idle.reduce(
        (chain, anchor) =>
          chain.then(() => this._prefetchSibling(anchor, { fullMatrix: true })),
        Promise.resolve()
      );
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 3000 });
      } else {
        setTimeout(run, 1200);
      }
    };

    // On mobile, this burst of sibling section + paginated-matrix fetches
    // saturates the phone's limited bandwidth and delays the WebGI environment
    // maps that gate the 3D viewer's first paint (measured: model-ready held to
    // ~11s while siblings loaded). Hold the warm until `ijewel:model-ready` so
    // the critical 3D assets win the bandwidth first. The prefetch itself is
    // unchanged — only its start is deferred; a metal switch before it runs
    // falls back to the existing reactive path (_switchTo awaits the matrix).
    // A long timeout still fires it on non-3D pages or a failed viewer boot so
    // siblings are never starved. Desktop keeps the original idle scheduling.
    const isMobile =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 767.98px), (pointer: coarse)').matches;

    if (!isMobile) {
      schedule();
      return;
    }

    let started = false;
    const startOnce = () => {
      if (started) return;
      started = true;
      schedule();
    };
    document.addEventListener('ijewel:model-ready', startOnce, { once: true });
    setTimeout(startOnce, 15000); // fallback: no 3D / boot failed
  }

  /**
   * Fetch (or return cached) sibling data fragment as raw JSON strings.
   *
   * Cached per handle for the element's lifetime (#cache): a sibling's data is
   * immutable within a page view, and this cache is what makes hover-warmed
   * switches instant and repeat switches free. It's small (one entry per metal)
   * so no eviction is warranted — bounding it would be complexity with no payoff.
   * @param {HTMLAnchorElement} anchor
   * @returns {Promise<{productJson: string, mapText: string, metafieldsText: string, ringSizeText: string, tcwMapText: string}>}
   */
  async _loadSibling(anchor) {
    const handle = anchor.dataset.siblingHandle || '';
    const cached = this.#cache.get(handle);
    if (cached) return cached;

    const base = (anchor.dataset.siblingUrl || anchor.getAttribute('href') || '').split('?')[0];
    const requestUrl = `${base}?view=${METAL_SWITCH_VIEW}`;

    // Parse the bare `layout none` payload as HTML and pull each script by id.
    // DOMParser (not JSON.parse) because the fragment is four <script> blobs,
    // not one JSON body — and this is the SAME path the initial PDP render uses
    // to read these ids, so there is exactly one parsing contract to maintain.
    const response = await fetch(requestUrl, { signal: this.#switchAbort?.signal });
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const productJson = doc.getElementById('metal-switch-product-json')?.textContent?.trim() || '';
    const mapText = doc.getElementById('metal-switch-ijewel-map')?.textContent?.trim() || '';
    const cadMapText = doc.getElementById('metal-switch-ijewel-cad-map')?.textContent?.trim() || '';
    const metafieldsText = doc.getElementById('metal-switch-metafields')?.textContent?.trim() || '';
    const ringSizeText = doc.getElementById('metal-switch-ring-size-map')?.textContent?.trim() || '';
    const tcwMapText = doc.getElementById('metal-switch-tcw-map')?.textContent?.trim() || '';
    const includedChecklistText =
      doc.getElementById('metal-switch-included-checklist-map')?.textContent?.trim() || '';
    if (!productJson) throw new Error(`No product JSON in metal-switch fragment for ${handle}`);

    const payload = {
      productJson,
      mapText,
      cadMapText,
      metafieldsText,
      ringSizeText,
      tcwMapText,
      includedChecklistText,
    };
    this.#cache.set(handle, payload);
    return payload;
  }

  /**
   * Apply the sibling's product-level metafields to any hero element tagged
   * `[data-product-metafield="namespace.key"]`. Sourced from the
   * `#metal-switch-metafields` blob (see snippets/product-reactive-metafields
   * -json.liquid) because `product | json` omits metafields. Scoped to this
   * section; no-clobber when a key is absent (leaves the server-rendered value).
   * @param {string} metafieldsText — raw JSON from the metal-switch fragment
   */
  _swapProductMetafields(metafieldsText) {
    if (!metafieldsText) return;
    let map;
    try {
      map = JSON.parse(metafieldsText);
    } catch (_e) {
      return;
    }
    const scope = this.closest('.shopify-section') || document;
    scope.querySelectorAll('[data-product-metafield]').forEach((el) => {
      const value = map[el.dataset.productMetafield];
      if (typeof value !== 'string') return;
      el.innerHTML = value;
    });
  }

  /**
   * Swap product-level content marked with [data-product-field] to the sibling
   * product's values during an in-place metal switch. product.description (and
   * other product-level fields) only change when the metal — i.e. the product —
   * changes, so this rides the same committed swap path as the JSON/iJewel-map
   * replacement rather than the per-variant `variant:update` flow. Scoped to
   * this section so related-products / other sections are untouched. Fields must
   * be HTML-valued product JSON properties (e.g. description).
   * @param {object} newProduct
   */
  _swapProductContent(newProduct) {
    if (!newProduct) return;
    const scope = this.closest('.shopify-section') || document;
    scope.querySelectorAll('[data-product-field]').forEach((el) => {
      const field = el.dataset.productField;
      const value = newProduct[field];
      if (typeof value !== 'string') return;
      el.innerHTML = value;
    });
  }

  /**
   * Orchestrate the in-place swap to a sibling metal.
   * @param {HTMLAnchorElement} anchor
   */
  async _switchTo(anchor) {
    this.#switchAbort?.abort();
    this.#switchAbort = new AbortController();

    this.classList.add('is-switching');
    try {
      const {
        productJson: rawJson,
        mapText,
        cadMapText,
        metafieldsText,
        ringSizeText,
        tcwMapText,
        includedChecklistText,
      } = await this._loadSibling(anchor);
      let productJson = rawJson;
      let newProduct = JSON.parse(productJson);
      const optionNames = newProduct.options || [];
      const siblingHandle = anchor.dataset.siblingHandle || '';

      const matrixApi = window.ProductVariantMatrix;
      const needsMatrix =
        matrixApi?.needsFullMatrix?.(newProduct) ||
        matrixApi?.isEmbeddedCap?.(newProduct) ||
        false;

      // High-variant rings hit Shopify's 250 inline cap — the section payload alone
      // cannot resolve shape/size after a shopper change. loadAll is cached per handle
      // (prefetch on idle/hover + PDP init), so repeat switches are cheap.
      if (siblingHandle && needsMatrix && matrixApi?.loadAll) {
        try {
          const matrix = await matrixApi.loadAll(siblingHandle);
          if (matrix.variants.length) {
            newProduct = {
              ...newProduct,
              variants: matrixApi.normalizeAll(matrix.variants, optionNames.length),
            };
            productJson = JSON.stringify(newProduct);
          }
        } catch (_e) {
          // Fall back to embedded sibling JSON from the section payload.
        }
      }

      const ppfRoot = document.querySelector('.product-purchase-form');
      const jsonNode = document.querySelector('.product-purchase-form__product-json');
      if (!ppfRoot || !jsonNode) {
        window.location.href = anchor.href; // no form on page → fall back
        return;
      }

      let selection = this._liveSelection(optionNames.length);
      const targetMetal = anchor.dataset.siblingMetal || '';
      if (targetMetal) {
        selection = this._adjustSelectionForMetal(selection, targetMetal, optionNames);
      }

      const matched = this._matchVariant(newProduct, selection);
      if (!matched) {
        window.location.href = anchor.href;
        return;
      }

      // Replace shared product JSON used by iJewel and compound accordions.
      jsonNode.textContent = productJson;
      if (siblingHandle) jsonNode.dataset.productHandle = siblingHandle;

      // Swap product-level content (description, etc.) to the sibling's values.
      this._swapProductContent(newProduct);

      // Swap product-level metafields (which product|json omits) into any
      // [data-product-metafield] hero element, from the metal-switch blob.
      this._swapProductMetafields(metafieldsText);

      // Swap the per-variant ring-size map so the metafield Size accordion
      // (product-ring-size-property.js) resolves sizes for the sibling's
      // variant ids. It re-reads this node on the variant:update that the
      // swap-product dispatch below triggers. No-op when neither product
      // uses the metafield size path (node absent / blob empty).
      const ringSizeNode = document.querySelector('.product-purchase-form__ring-size-map');
      if (ringSizeNode && ringSizeText) ringSizeNode.textContent = ringSizeText;

      const tcwMapNode = document.querySelector('.product-purchase-form__tcw-map');
      if (tcwMapNode) tcwMapNode.textContent = tcwMapText || '{}';

      // Swap Included Checklist flags so variant:update after the metal
      // switch resolves against sibling variant ids (first 250). Full matrix
      // merge follows via product-variants:loaded when needed.
      const includedChecklistNode = document.querySelector('.product-included-checklist__map');
      if (includedChecklistNode && includedChecklistText) {
        includedChecklistNode.textContent = includedChecklistText;
      }

      // Seed iJewel map from the switch payload (sibling's selected variant),
      // merged so previously resolved siblings' entries survive.
      const bootstrapMap = mapText ? JSON.parse(mapText) : {};
      window.iJewelVariantMap = matrixApi?.mergeMaps
        ? matrixApi.mergeMaps(window.iJewelVariantMap, bootstrapMap)
        : Object.assign({}, window.iJewelVariantMap, bootstrapMap);
      // Layer A for the sibling — REPLACED, not merged: variant ids belong to
      // one product, and a stale id→cad pair would resolve the wrong geometry.
      if (cadMapText) {
        try {
          window.iJewelVariantCadMap = JSON.parse(cadMapText);
        } catch (_e) {
          /* keep the previous map rather than dropping 3D entirely */
        }
      }
      const ijewelRoot = document.querySelector('[data-ijewel-root]');
      if (ijewelRoot && siblingHandle) ijewelRoot.dataset.productHandle = siblingHandle;
      // The bridge's refresh-context handler calls IjewelCadResolver.recommit(),
      // re-applying every already-resolved CAD onto the sibling's variant ids —
      // so a metal switch downloads no GLB it has already seen. The paginated
      // full-map fetch that used to run here is gone: it could never complete
      // (each page was itself capped at ~20 distinct metaobjects), and per-CAD
      // resolution covers the sibling without it.
      document.dispatchEvent(new CustomEvent('ijewel:refresh-context'));

      const ppfInstance = ppfRoot._productPurchaseFormInstance;

      // Point the variant engine at the sibling matrix before syncing options,
      // so any change handlers resolve against the new product — not the old one.
      if (ppfInstance && typeof ppfInstance.repointProduct === 'function') {
        ppfInstance.repointProduct(newProduct);
      } else {
        document.dispatchEvent(
          new CustomEvent('product-form:repoint-product', {
            detail: { root: ppfRoot, json: newProduct },
          })
        );
      }

      // Sync non-compound options first so the sibling metal is on the form.
      const nonCompound = selection.map((value, i) => {
        const name = optionNames[i] || '';
        return name.indexOf(':') !== -1 ? null : value;
      });
      if (ppfInstance && typeof ppfInstance.syncOptionSelection === 'function') {
        ppfInstance.syncOptionSelection(nonCompound);
      }

      // Push the full compound value (shape + secondary metal + length) onto the
      // hidden select BEFORE re-initing the accordion bridge — otherwise its init
      // pass resolves against a stale compound and can snap shape back to default.
      if (ppfInstance && typeof ppfInstance.syncOptionSelection === 'function') {
        ppfInstance.syncOptionSelection(selection);
      }

      // Re-bind compound accordions against the sibling variant matrix.
      if (typeof window.initCompoundAccordions === 'function') {
        window.initCompoundAccordions(document, newProduct.variants || []);
      }

      // Re-match from the live form so shape / length / stone type persist in
      // the sibling variant id (and therefore in the 3D cad lookup).
      const finalSelection = this._liveSelection(optionNames.length);
      const finalMatched =
        this._matchVariant(newProduct, finalSelection) || matched;

      // Repoint the purchase form at the sibling + matched variant. This
      // fires variant:update → 3D applies the new metal in place, price/ATC
      // update.
      document.dispatchEvent(
        new CustomEvent('product-form:swap-product', {
          detail: { root: ppfRoot, json: newProduct, variant: finalMatched },
        })
      );

      // Keep URL in sync without navigating.
      const base = (anchor.dataset.siblingUrl || anchor.href).split('?')[0];
      window.history.replaceState({}, '', `${base}?variant=${finalMatched.id}`);

      // Keep the tab title in sync too — the URL now says the sibling metal
      // but a stale document.title (e.g. "… 14K Yellow Gold" after switching
      // to white gold) reads as a bug in the tab bar, share sheets and
      // history. Preserve the "… – SHOP NAME" suffix from the current title.
      if (newProduct.title) {
        const titleParts = document.title.split(' – ');
        const titleSuffix =
          titleParts.length > 1 ? ' – ' + titleParts[titleParts.length - 1] : '';
        document.title = newProduct.title + titleSuffix;
      }
    } finally {
      this.classList.remove('is-switching');
    }
  }

  /** cad handle for the currently selected variant — geometry key for 3D. */
  _currentCad() {
    const idInput = document.querySelector(
      '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]'
    );
    const id = idInput?.value;
    if (!id) return '';
    // Resolver knows both layers (resolved GLB map + lightweight cad map), so a
    // shape whose GLB has not been resolved yet still reports its cad.
    if (window.IjewelCadResolver) return window.IjewelCadResolver.cadForVariant(id) || '';
    return window.iJewelVariantMap?.[String(id)]?.cad || '';
  }

  /**
   * Live option values from the purchase form (includes compound hidden select).
   * @param {number} optionCount
   * @returns {(string|null)[]}
   */
  _liveSelection(optionCount) {
    const selected = new Array(optionCount).fill(null);
    document
      .querySelectorAll('.product-purchase-form [data-option-select]')
      .forEach((el) => {
        const pos = parseInt(el.dataset.optionPosition, 10);
        if (!pos) return;
        const idx = pos - 1;

        if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
          const hidden = el.querySelector('input[type="hidden"]');
          if (hidden) selected[idx] = hidden.value;
        } else if (el.tagName === 'SELECT') {
          selected[idx] = el.value;
        } else if (el.type === 'radio') {
          if (el.checked) selected[idx] = el.value;
        } else if (el.value) {
          selected[idx] = el.value;
        }
      });
    return selected;
  }

  /**
   * Rewrite Metal Type + compound secondary-metal parts for the target sibling.
   * @param {(string|null)[]} selection
   * @param {string} targetMetal
   * @param {string[]} optionNames
   */
  _adjustSelectionForMetal(selection, targetMetal, optionNames) {
    const out = selection.slice();
    for (let i = 0; i < optionNames.length; i++) {
      const name = optionNames[i];
      if (!name) continue;

      if (name.indexOf(':') === -1 && /metal/i.test(name)) {
        out[i] = targetMetal;
        continue;
      }

      if (name.indexOf(':') === -1 || !out[i]) continue;

      const subNames = name.split(':').map((part) => part.trim());
      const subValues = String(out[i]).split(' / ');
      let changed = false;
      for (let k = 0; k < subNames.length; k++) {
        if (/metal/i.test(subNames[k])) {
          subValues[k] = targetMetal;
          changed = true;
        }
      }
      if (changed) out[i] = subValues.join(' / ');
    }
    return out;
  }

  /**
   * Find the sibling variant matching the shopper's current selection.
   * @param {object} product
   * @param {(string|null)[]} selection
   */
  _matchVariant(product, selection) {
    const variants = product.variants || [];
    const optionNames = product.options || [];
    if (!variants.length) return null;
    if (!selection.length) {
      return variants.find((v) => v.available) || variants[0];
    }

    const optionValue = (variant, position) =>
      window.ProductVariantMatrix?.optionValue(variant, position) ??
      variant['option' + position];

    const matchesSelection = (variant, skipEmptyVariantOptions) =>
      selection.every((val, i) => {
        if (val == null || val === '') return true;
        const variantVal = optionValue(variant, i + 1);
        if (variantVal == null && skipEmptyVariantOptions) return true;
        return variantVal === val;
      });

    const exact = variants.find((v) => matchesSelection(v, false));
    if (exact) return exact;

    const partial = variants.filter((v) => matchesSelection(v, true));
    if (partial.length === 1) return partial[0];
    if (partial.length) {
      const compoundIdx = optionNames.findIndex(
        (name) => name && String(name).indexOf(':') !== -1
      );
      if (compoundIdx >= 0 && selection[compoundIdx]) {
        const compoundMatches = partial.filter(
          (v) => optionValue(v, compoundIdx + 1) === selection[compoundIdx]
        );
        if (compoundMatches.length) {
          return compoundMatches.find((v) => v.available) || compoundMatches[0];
        }
      }
      return partial.find((v) => v.available) || partial[0];
    }

    // Ring / metafield siblings: keep Stone Type + Shape (+ Ring Size when set).
    // selection[] is 0-based by option position: [0]=Metal, [1]=Stone Type,
    // [2]=Stone Shape, [3]=Ring Size — NOT option indices 1-2 (that was Metal+Type).
    const partialStoneShape = (avail) => {
      const candidates = variants.filter((v) => {
        if (selection[1] != null && selection[1] !== '' && optionValue(v, 2) !== selection[1]) {
          return false;
        }
        if (selection[2] != null && selection[2] !== '' && optionValue(v, 3) !== selection[2]) {
          return false;
        }
        if (selection[3] != null && selection[3] !== '' && optionValue(v, 4) !== selection[3]) {
          return false;
        }
        if (avail && !v.available) return false;
        return true;
      });
      if (!candidates.length) return null;
      if (selection[0] != null && selection[0] !== '') {
        const metalMatch = candidates.find((v) => optionValue(v, 1) === selection[0]);
        if (metalMatch) return metalMatch;
      }
      return candidates.find((v) => v.available) || candidates[0];
    };
    const kept = partialStoneShape(true) || partialStoneShape(false);
    if (kept) return kept;

    return variants.find((v) => v.available) || variants[0];
  }

  /**
   * Move the active state + label to the chosen sibling.
   * @param {HTMLAnchorElement} anchor
   */
  _setActive(anchor) {
    this._siblings?.forEach((el) => {
      const active = el === anchor;
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
    const nameEl = this.querySelector('[data-current-name]');
    if (nameEl && anchor.dataset.siblingMetal) {
      nameEl.textContent = anchor.dataset.siblingMetal;
      this._committedName = anchor.dataset.siblingMetal;
    }
    const priceEl = this.querySelector('[data-current-price]');
    // Commit shows the metal's CONTRIBUTION (product-price-contributions.js);
    // the engine recomputes it again on variant:update.
    const committedPrice = anchor.dataset.siblingContribution;
    if (priceEl && committedPrice != null) {
      priceEl.textContent = committedPrice;
      this._committedPrice = committedPrice;
    }
    const bg = anchor.querySelector('.product-option-accordion__swatch-bg');
    if (bg) {
      const style = bg.getAttribute('style') || '';
      this._paintSwatch(style);
      this._committedSwatch = style;
      this._swatchPopulated = true;
    }
    this.querySelector('details.product-option-accordion')?.classList.add(
      'product-option-accordion--selected'
    );
  }

  /**
   * Set the committed summary price from outside (product-price-
   * contributions.js writes the current metal's contribution here so
   * hover previews restore to it instead of a stale value).
   * @param {string} text
   */
  setCommittedPrice(text) {
    this._committedPrice = text;
    const priceEl = this.querySelector('[data-current-price]');
    if (priceEl) priceEl.textContent = text;
  }
}

if (!customElements.get('product-group-selector-component')) {
  customElements.define('product-group-selector-component', ProductGroupSelectorComponent);
}
